import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { EmergencyFundAssetPickStep } from '../../components/emergencyFund/EmergencyFundAssetPickStep';
import { EmergencyFundDoneStep } from '../../components/emergencyFund/EmergencyFundDoneStep';
import { EmergencyFundExpensesStep } from '../../components/emergencyFund/EmergencyFundExpensesStep';
import { EmergencyFundIntroStep } from '../../components/emergencyFund/EmergencyFundIntroStep';
import { EmergencyFundMonthsStep } from '../../components/emergencyFund/EmergencyFundMonthsStep';
import { WizardSkeleton, WizardStepFooter } from '../../components/wizard';
import {
  emergencyFundKeys,
  useAdvanceWizardMutation,
  useAllocationSetupQuery,
  useBulkAllocationsMutation,
  useBuildGoalSuggestionQuery,
  useEmergencyFundConfigQuery,
  useEmergencyFundTargetEstimateQuery,
  useFundHealthQuery,
  useUpdateConfigMutation,
} from '../../features/emergencyFund/queries';
import { useSnapshotsTimelineQuery } from '../../features/snapshots/queries';
import {
  buildCustomEfConfigPayload,
  clampMonths,
  computeTotalTarget,
  parseAmount,
  RECOMMENDED_MONTHS,
} from '../../lib/emergencyFundDisplayUtils';
import pt from '../../locales/pt';
import { paths } from '../../routes/paths';

const WIZARD_STEPS = ['intro', 'expenses', 'months', 'allocations', 'done'];

function resolveInitialStepIndex(wizardStep, searchStep) {
  if (searchStep && WIZARD_STEPS.includes(searchStep)) {
    return WIZARD_STEPS.indexOf(searchStep);
  }
  switch (wizardStep) {
    case 'ef_target_done':
      return WIZARD_STEPS.indexOf('allocations');
    case 'allocations_done':
      return WIZARD_STEPS.indexOf('done');
    case 'completed':
      return WIZARD_STEPS.indexOf('done');
    default:
      return 0;
  }
}

export function EmergencyFundWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromGuided = searchParams.get('from') === 'guided';
  const qc = useQueryClient();

  const { data: patrimonioTimeline = [] } = useSnapshotsTimelineQuery();
  const hasSelfie = patrimonioTimeline.length > 0;
  const { data: config } = useEmergencyFundConfigQuery();
  const { data: estimate } = useEmergencyFundTargetEstimateQuery({ enabled: hasSelfie });
  const { data: health } = useFundHealthQuery({ enabled: hasSelfie });
  const { data: suggestion } = useBuildGoalSuggestionQuery({ enabled: hasSelfie });
  const updateConfig = useUpdateConfigMutation();
  const advanceWizard = useAdvanceWizardMutation();
  const bulkSave = useBulkAllocationsMutation();
  const allocationRef = useRef(null);
  const { data: setup } = useAllocationSetupQuery();

  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [resumeApplied, setResumeApplied] = useState(false);
  const [avgMonthly, setAvgMonthly] = useState('');
  const [months, setMonths] = useState(RECOMMENDED_MONTHS);
  const [savedTotal, setSavedTotal] = useState(0);
  const avgMonthlyInitialized = useRef(false);

  const displaySavedTotal = useMemo(() => {
    if (savedTotal > 0) return savedTotal;
    if (!setup?.accounts) return 0;
    return setup.accounts.reduce(
      (acc, row) =>
        acc + parseAmount(row.base_amount_eur) + parseAmount(row.ef_amount_eur),
      0,
    );
  }, [savedTotal, setup]);

  // Prefer total avg (same metric as the dashboard) when enough selfies exist;
  // fall back to essential avg for users who still haven't reached 3 selfies.
  const estimateAvg =
    Number(health?.avg_monthly_expenses || 0) ||
    Number(estimate?.essential_monthly_avg || 0);
  const isWizardCompleted = config?.wizard_step === 'completed';

  useEffect(() => {
    if (!config) return;
    if (!hasSelfie && !isWizardCompleted) {
      navigate(paths.emergencyFund, { replace: true });
    }
  }, [config, hasSelfie, isWizardCompleted, navigate]);

  useEffect(() => {
    if (!config || resumeApplied) return;
    const idx = resolveInitialStepIndex(config.wizard_step, searchParams.get('step'));
    setStepIndex(idx);
    setResumeApplied(true);
  }, [config, resumeApplied, searchParams]);

  useEffect(() => {
    if (!estimate && !config) return;

    const avg =
      config?.ef_custom_target && config?.ef_months_of_coverage
        ? Number(config.ef_custom_target) / config.ef_months_of_coverage
        : estimateAvg > 0
          ? estimateAvg
          : 0;

    if (avg > 0 && !avgMonthlyInitialized.current) {
      setAvgMonthly(String(Math.round(avg)));
      avgMonthlyInitialized.current = true;
    }

    if (config?.ef_months_of_coverage) {
      setMonths(clampMonths(config.ef_months_of_coverage));
    }
  // avgMonthly excluído intencionalmente — init-only, não deve re-correr quando o utilizador limpa o campo
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate, config, estimateAvg]);

  const stepKey = WIZARD_STEPS[stepIndex];
  const copy = pt.emergencyFund.wizard;
  const avgNum = parseAmount(avgMonthly);
  const totalTarget = computeTotalTarget(avgMonthly, months);
  const isDone = stepKey === 'done';

  const exitPath = fromGuided ? `${paths.dashboard}?openGuidedFlow=1` : paths.emergencyFund;

  const goTo = useCallback((nextIndex) => {
    setDirection(nextIndex < stepIndex ? 'back' : 'forward');
    setStepIndex(nextIndex);
  }, [stepIndex]);

  const onBack = useCallback(() => {
    if (stepIndex === 0) {
      navigate(exitPath);
      return;
    }
    goTo(stepIndex - 1);
  }, [exitPath, goTo, navigate, stepIndex]);

  const onContinue = useCallback(async () => {
    try {
      if (stepKey === 'intro') {
        goTo(stepIndex + 1);
        return;
      }

      if (stepKey === 'expenses') {
        if (avgNum <= 0) {
          toast.error(copy.splitAvgRequired);
          return;
        }
        goTo(stepIndex + 1);
        return;
      }

      if (stepKey === 'months') {
        if (avgNum <= 0) {
          toast.error(copy.splitAvgRequired);
          return;
        }
        if (totalTarget <= 0) {
          toast.error(copy.monthsRequired);
          return;
        }

        await updateConfig.mutateAsync(buildCustomEfConfigPayload(avgMonthly, months));

        const wizardStep = config?.wizard_step;
        if (!wizardStep || wizardStep === 'not_started') {
          await advanceWizard.mutateAsync('base_fund_done');
        }
        if (
          wizardStep !== 'ef_target_done' &&
          wizardStep !== 'allocations_done' &&
          wizardStep !== 'completed'
        ) {
          await advanceWizard.mutateAsync('ef_target_done');
        }
        goTo(stepIndex + 1);
        return;
      }

      if (stepKey === 'allocations') {
        await allocationRef.current?.save();
        if (config?.wizard_step !== 'allocations_done' && config?.wizard_step !== 'completed') {
          await advanceWizard.mutateAsync('allocations_done');
        }
        const nextSaved = allocationRef.current?.getSelectedTotal?.() ?? 0;
        setSavedTotal(nextSaved);
        goTo(stepIndex + 1);
        return;
      }

      if (stepKey === 'done') {
        if (config?.wizard_step !== 'completed') {
          await advanceWizard.mutateAsync('completed');
        }
        // Kick off re-fetches so the dashboard sees fresh data immediately on mount.
        // Config and allocationSetup may already be fresh from wizard activity, but
        // health has no subscriber here — without this it would only refetch after
        // the dashboard subscribes, causing a flash of stale data.
        void qc.refetchQueries({ queryKey: emergencyFundKeys.config(), exact: true });
        void qc.refetchQueries({ queryKey: emergencyFundKeys.health(), exact: true });
        void qc.refetchQueries({ queryKey: emergencyFundKeys.allocationSetup(), exact: true });
        toast.success(copy.savedToast);
        navigate(exitPath, { replace: true });
      }
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Erro ao guardar.');
    }
  }, [
    advanceWizard,
    avgMonthly,
    avgNum,
    config?.wizard_step,
    copy,
    exitPath,
    goTo,
    months,
    navigate,
    qc,
    stepIndex,
    stepKey,
    totalTarget,
    updateConfig,
  ]);

  const onSkipAllocations = useCallback(async () => {
    try {
      if (config?.wizard_step !== 'allocations_done' && config?.wizard_step !== 'completed') {
        await advanceWizard.mutateAsync('allocations_done');
      }
      setSavedTotal(0);
      goTo(stepIndex + 1);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Erro ao guardar.');
    }
  }, [advanceWizard, config?.wizard_step, goTo, stepIndex]);

  const footerLabel = useMemo(() => {
    if (stepKey === 'intro') return copy.intro.cta;
    if (stepKey === 'months') return copy.defineTargetCta;
    if (stepKey === 'done') return copy.done.cta;
    return copy.continueCta;
  }, [stepKey, copy]);

  const isPending =
    updateConfig.isPending || advanceWizard.isPending || bulkSave.isPending;

  return (
    <WizardSkeleton
      appChrome
      wizardTitle={copy.title}
      step={stepIndex + 1}
      total={WIZARD_STEPS.length}
      onBack={onBack}
      showBack={!isDone}
      direction={direction}
      loading={isPending}
      showProgress={!isDone}
      showPercent={false}
      footer={
        <WizardStepFooter
          primaryLabel={footerLabel}
          onPrimary={onContinue}
          onSkip={stepKey === 'allocations' ? onSkipAllocations : undefined}
          skipLabel={copy.skipLater}
          loading={isPending}
        />
      }
    >
      {stepKey === 'intro' ? <EmergencyFundIntroStep /> : null}
      {stepKey === 'expenses' ? (
        <EmergencyFundExpensesStep
          value={avgMonthly}
          onChange={setAvgMonthly}
          prefillAvg={estimateAvg}
        />
      ) : null}
      {stepKey === 'months' ? (
        <EmergencyFundMonthsStep
          months={months}
          onMonthsChange={setMonths}
          avgMonthly={avgMonthly}
        />
      ) : null}
      {stepKey === 'allocations' ? (
        <EmergencyFundAssetPickStep
          ref={allocationRef}
          bulkSave={bulkSave}
          targetEur={totalTarget}
        />
      ) : null}
      {stepKey === 'done' ? (
        <EmergencyFundDoneStep
          avgMonthly={avgMonthly}
          months={months}
          savedTotal={displaySavedTotal}
          suggestedMonthly={suggestion?.suggested_monthly_contribution}
          monthsHorizon={suggestion?.months_horizon || months}
        />
      ) : null}
    </WizardSkeleton>
  );
}

export default EmergencyFundWizard;
